<template>
  <a-modal :title="modalTitle" :visible="visible" :confirm-loading="loading" @ok="handleOk" @cancel="handleCancel"
    @afterClose="closeModal" okText="确认" cancelText="取消" width="560px" destroyOnClose>
    <a-form ref="formRef" :model="formState" :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">

      <a-form-item label="标题" name="name">
        <a-input v-model:value="formState.name" placeholder="请输入标题（如：快递信息、商品提示）" maxlength="50" />
        <div class="form-hint">提示：标题留空时，商城页面将不显示标题，直接展示文字或图片。</div>
      </a-form-item>

      <a-form-item label="内容类型" name="contentType">
        <a-radio-group v-model:value="contentType" @change="handleContentTypeChange">
          <a-radio value="text">文字</a-radio>
          <a-radio value="image">图片</a-radio>
        </a-radio-group>
      </a-form-item>

      <!-- 文字内容：传递给 description -->
      <a-form-item v-if="contentType === 'text'" label="文字内容" name="description"
        :rules="[{ required: true, message: '请输入文字内容' }, { validator: descriptionValidator }]">
        <a-textarea v-model:value="formState.description" :rows="4" placeholder="请输入附加信息文字内容" maxlength="500"
          show-count />
      </a-form-item>

      <!-- 图片内容：上传后保存 imageUuid -->
      <a-form-item v-else label="图片" name="imageUuid" :rules="[{ required: true, message: '请上传图片' }]">
        <a-upload v-model:file-list="fileList" name="file" list-type="picture-card" :show-upload-list="false"
          :custom-request="uploadImage" :before-upload="beforeUpload" class="upload-wrapper">
          <div v-if="formState.imageUuid" class="upload-preview">
            <img :src="imageUrl + formState.imageUuid" alt="图片" />
            <div class="upload-actions" @click.stop="removeImage">
              <DeleteOutlined />
            </div>
          </div>
          <div v-else>
            <loading-outlined v-if="uploading"></loading-outlined>
            <plus-outlined v-else></plus-outlined>
            <div class="ant-upload-text">上传图片</div>
          </div>
        </a-upload>
      </a-form-item>

      <a-form-item v-if="isEdit" label="ID" name="id" style="display: none;">
        <a-input v-model:value="formState.id" disabled />
      </a-form-item>

    </a-form>
  </a-modal>
</template>
<script>
import { PlusOutlined, LoadingOutlined, DeleteOutlined } from "@ant-design/icons-vue";
import { AddMallSetting, UpdateMallSetting } from "/src/api/shopping.js";
import { Upload } from "/src/api/upload.js";
import { message } from "ant-design-vue";

const CATEGORY_NAME = "商品展示信息";

export default {
  name: "商品展示信息新增编辑弹窗",
  components: {
    PlusOutlined,
    LoadingOutlined,
    DeleteOutlined,
  },
  props: {
    visible: { type: Boolean, default: false },
    record: { type: Object, default: () => ({}) },
  },
  emits: ['update:visible', 'ok'],
  data() {
    return {
      loading: false,
      uploading: false,
      imageUrl: '/api/file/img/',
      thumbnailUrl: '/api/file/img/thumbnail/',
      fileList: [],
      contentType: 'text',
      formState: {
        id: undefined,
        name: '',
        description: '',
        imageUuid: '',
      },
      formRef: null,
    };
  },
  computed: {
    isEdit() {
      return !!this.formState.id;
    },
    modalTitle() {
      return (this.isEdit ? "编辑" : "新增") + "商品展示信息";
    },
  },
  watch: {
    record: {
      handler(newRecord) {
        if (newRecord && newRecord.id) {
          // 编辑：根据记录内容推断内容类型（有图则为图片，否则为文字）
          const hasImage = !!newRecord.imageUuid;
          this.contentType = hasImage ? 'image' : 'text';
          this.formState = {
            id: newRecord.id,
            name: newRecord.name || '',
            description: hasImage ? '' : (newRecord.description || ''),
            imageUuid: hasImage ? newRecord.imageUuid : '',
          };
        } else {
          this.resetFormState();
        }
      },
      immediate: true,
      deep: true,
    }
  },
  methods: {
    resetFormState() {
      this.contentType = 'text';
      this.formState = {
        id: undefined,
        name: '',
        description: '',
        imageUuid: '',
      };
      this.fileList = [];
      this.uploading = false;
    },

    // 表单校验：图片类型时无需校验文字内容
    descriptionValidator(rule, value) {
      if (this.contentType === 'image') {
        return Promise.resolve();
      }
      if (!value || !String(value).trim()) {
        return Promise.reject(new Error('请输入文字内容'));
      }
      return Promise.resolve();
    },

    // 切换内容类型时，清空另一类型的值，避免脏数据
    handleContentTypeChange() {
      if (this.contentType === 'text') {
        this.formState.imageUuid = '';
        this.fileList = [];
      } else {
        this.formState.description = '';
      }
    },

    removeImage() {
      this.formState.imageUuid = '';
      this.fileList = [];
      message.success('图片已移除');
    },

    beforeUpload(file) {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('只能上传 JPG/PNG 文件!');
      }
      return isJpgOrPng;
    },

    async uploadImage({ file, onSuccess, onError }) {
      this.uploading = true;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'portrait');
      formData.append('system', 'true');

      try {
        const res = await Upload(formData);
        if (res.errCode === 0 && res.data) {
          this.formState.imageUuid = res.data;
          message.success('图片上传成功');
          onSuccess(res);
        } else {
          message.error('上传失败：' + (res.message || '未知错误'));
          onError(new Error('Upload failed'));
        }
      } catch (error) {
        console.error("uploadImage error:", error);
        message.error('网络错误，上传失败');
        onError(error);
      } finally {
        this.uploading = false;
      }
    },

    async handleOk() {
      try {
        await this.$refs.formRef.validate();
        this.loading = true;

        // 组装统一接口参数（category 固定为「商品展示信息」）
        // 注意：后端不接受 imageUuid / description 为空，因此只提交当前内容类型对应的字段
        const payload = {
          id: this.formState.id,
          name: this.formState.name,
          category: CATEGORY_NAME,
        };

        if (this.contentType === 'image') {
          payload.imageUuid = this.formState.imageUuid;
        } else {
          payload.description = this.formState.description;
        }

        let res;
        if (this.isEdit) {
          res = await UpdateMallSetting(payload);
        } else {
          delete payload.id;
          res = await AddMallSetting(payload);
        }

        if (res.errCode === 0) {
          message.success("操作成功");
          this.resetFormState();
          this.$emit('ok');
          this.$emit('update:visible', false);
        } else {
          message.error(res.errMsg || "操作失败");
        }
      } catch (error) {
        if (error && error.errorFields && error.errorFields.length > 0) {
          const firstErrorMsg = error.errorFields[0].errors[0];
          message.warning(`请检查表单：${firstErrorMsg}`);
        } else if (error !== false) {
          console.error('API Error:', error);
          message.error('请求异常，请稍后重试');
        }
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$emit('update:visible', false);
    },

    closeModal() {
      this.resetFormState();
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields();
      }
    },
  },
};
</script>

<style scoped>
.form-hint {
  color: #999;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;
}

.upload-wrapper {
  width: 104px;
  height: 104px;
}

.upload-preview {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.upload-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 5;
}

.upload-preview:hover .upload-actions {
  opacity: 1;
}
</style>
